/**
 * useActiveJobs — Subscribes to the user's active processing jobs from Firestore.
 *
 * Replaces the single-job useProcessingListener for the ProcessingPill.
 * Subscribes to `processingJobs` where userId matches and status is active.
 *
 * Returns an array of job objects and fires callbacks on job completion/error.
 *
 * See: docs/features/infrastructure/backend-processing.md (Phase 12)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../utils/firebase";

// Default estimated processing time (60s) when backend doesn't provide one
const DEFAULT_ESTIMATED_MS = 60_000;

/**
 * @param {Object}   params
 * @param {string}   params.userId — Firebase Auth UID
 * @param {Object}   [params.callbacks]
 * @param {Function} [params.callbacks.onJobComplete] — (job) => void
 * @param {Function} [params.callbacks.onJobError]    — (job) => void
 * @returns {{ jobs: Array, activeCount: number, dismiss: Function }}
 */
export function useActiveJobs({ userId, callbacks } = {}) {
  const [jobs, setJobs] = useState([]);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Track previous statuses to fire callbacks only on transitions
  const prevStatusMapRef = useRef(new Map());
  // Track dismissed job IDs (persists across renders, clears on unmount)
  const dismissedRef = useRef(new Set());

  const dismiss = useCallback((jobId) => {
    dismissedRef.current.add(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  useEffect(() => {
    if (!userId) {
      setJobs([]);
      return;
    }

    const jobsRef = collection(db, "processingJobs");
    const q = query(
      jobsRef,
      where("userId", "==", userId),
      where("status", "in", ["queued", "processing"])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const prevMap = prevStatusMapRef.current;
        const newJobs = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const jobId = doc.id;

          // Skip dismissed jobs
          if (dismissedRef.current.has(jobId)) return;

          const prevStatus = prevMap.get(jobId);
          const newStatus = data.status;

          // Compute time-based progress
          const startedAt = data.startedAt?.toMillis?.() || 0;
          const estimatedTotalMs = DEFAULT_ESTIMATED_MS;
          let progressPercent = 0;
          if (startedAt && newStatus === "processing") {
            const elapsed = Date.now() - startedAt;
            progressPercent = Math.min(95, Math.round((elapsed / estimatedTotalMs) * 100));
          }

          newJobs.push({
            id: jobId,
            patientId: data.patientId || "",
            patientName: data.patientName || "",
            documentId: data.documentId || "",
            docTypeId: data.docTypeId || "",
            docTitle: data.docTitle || "",
            sourceType: data.sourceType || "Document",
            status: newStatus,
            statusLabel: data.statusLabel || "",
            currentStep: data.currentStep || 0,
            totalSteps: data.totalSteps || 0,
            startedAt,
            estimatedTotalMs,
            progressPercent,
            createdAt: data.createdAt?.toMillis?.() || 0,
          });

          // Update status tracking
          prevMap.set(jobId, newStatus);
        });

        // Sort by createdAt (newest first)
        newJobs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setJobs(newJobs);
      },
      (error) => {
        console.error("[useActiveJobs] Snapshot error:", error);
      }
    );

    return () => {
      unsubscribe();
      prevStatusMapRef.current.clear();
    };
  }, [userId]);

  // Detect completed/failed jobs via snapshot removals
  // When a job transitions to "completed" or "failed", it leaves the query
  // results (since we filter on status in ["queued", "processing"]).
  // We detect this by comparing current jobs with previous jobs.
  const prevJobIdsRef = useRef(new Set());
  useEffect(() => {
    const currentIds = new Set(jobs.map((j) => j.id));
    const prevIds = prevJobIdsRef.current;

    // Jobs that were in previous set but not in current (and not dismissed)
    // means they transitioned to completed or failed
    for (const prevId of prevIds) {
      if (!currentIds.has(prevId) && !dismissedRef.current.has(prevId)) {
        // Job completed or failed — we fire a generic completion callback
        // The caller can determine specifics from the notification system
        callbacksRef.current?.onJobComplete?.({ id: prevId });
      }
    }

    prevJobIdsRef.current = currentIds;
  }, [jobs]);

  // Refresh progress percentages periodically (time-based)
  useEffect(() => {
    if (jobs.length === 0) return;

    const timer = setInterval(() => {
      setJobs((prev) =>
        prev.map((job) => {
          if (!job.startedAt || job.status !== "processing") return job;
          const elapsed = Date.now() - job.startedAt;
          const progressPercent = Math.min(95, Math.round((elapsed / job.estimatedTotalMs) * 100));
          if (progressPercent === job.progressPercent) return job;
          return { ...job, progressPercent };
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [jobs.length]);

  return {
    jobs,
    activeCount: jobs.length,
    dismiss,
  };
}
